import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, inject } from '@angular/core';
import { App } from '@df/core/app';
import { Acl } from '@df/core/service/acl';
import { resizeObservable } from '@df/dom/fn/resize-observable';
import { NDfDomGlobals, WINDOW } from '@df/dom/globals';
import { TimeoutService } from '@df/ng/timeout.service';
import findLast from 'lodash-es/findLast';
import { BehaviorSubject, Subject, Subscription, fromEvent, merge } from 'rxjs';
import { NCore } from '../df-core.interfaces';

export type ISizeName = 'S' | 'M' | 'L' | 'X';
export type ISizeId = 0 | 1 | 2 | 3;

/**
 * Client mode
 * browser - regular browser
 * universal - universal (node side), quick, short
 * prerender - prerender (node side), generated with ng-ssr, full copy for bots
 * ssr - ssr mode, unified output for both users and bots (nide, based on ng-ssr)
 * legacyPrerender - legacy prerender mode, generated with old prerender library (node, headless chrome)
 */
export type TClientMode = 'browser' | 'universal' | 'prerender' | 'ssr' | 'legacyPrerender';

export const RETINA_MEDIA_QUERY = `(-webkit-min-device-pixel-ratio: 1.5),
                (min--moz-device-pixel-ratio: 1.5),
                (-o-min-device-pixel-ratio: 3/2),
                (min-resolution: 1.5dppx)`;

export const isPrerenderOld =
    typeof window !== 'undefined' &&
    ((typeof window.navigator?.userAgent === 'string' && /prerender/i.test(window.navigator?.userAgent)) ||
        (typeof document !== 'undefined' && !!document.cookie.match(/isPrerender/)));
/**
 * Client state service
 * used to identify browser/client status.
 *
 * Access it either directly importing
 * or via app.client
 *
 * @export
 * @class Client
 */
@Injectable({
    providedIn: 'root'
})
export class Client {
    /**
     * If current brakpoint is S.
     *
     * @type {boolean}
     */
    isS!: boolean;

    /**
     * If current brakpoint is M.
     *
     * @type {boolean}
     */
    isM!: boolean;

    /**
     * If current brakpoint is L.
     *
     * @type {boolean}
     */
    isL!: boolean;

    /**
     * If current brakpoint is X.
     *
     * @type {boolean}
     */
    isX!: boolean;

    /**
     * String identifier of breakpoint S|M|L|X.
     *
     * @type {boolean}
     */
    size!: ISizeName;

    /**
     * Number identifier of brakpoint 0|1|2|3.
     *
     * @type {ISizeId}
     */
    sizeId!: ISizeId;

    /**
     * Is browser as prerender.
     *
     * @deprecated Use mode === 'prerender' or mode === 'legacyPrerender' instead
     * @type {boolean}
     */
    isPrerender: boolean;

    /**
     * Is browser as universal.
     *
     * @deprecated Use mode === 'universal' instead
     * @type {boolean}
     */
    isUniversal: boolean;

    /**
     * Client mode
     * @see TClientMode
     */
    mode: TClientMode;

    /**
     * @deprecated Use mode !== 'browser' instead
     * @type {boolean}
     */
    isBot?: boolean;

    /**
     * Does browser support touchy devices.
     *
     * @type {boolean}
     */
    isTouchy!: boolean;

    /**
     * Is display retina.
     *
     * @type {boolean}
     */
    isRetina: boolean;

    /**
     * If cms is currently being in user
     */
    readonly isCms$ = new BehaviorSubject(false);
    readonly isCmsPreview$ = new BehaviorSubject(false);

    /**
     * Forces cms app style headers in client app
     */
    forceCmsRequests = false;
    /**
     * Observables
     */
    obs: {
        /**
         * Subscribe to this observble for resize changes.
         *
         * @type {Subject<Event>}
         */
        resize: Subject<Event | void>;

        /**
         * Subscribe to this observable for brakpoint changes.
         *
         * @type {Observable<ISizeName>}
         */
        breakpoint: Subject<ISizeName>;

        /**
         * Viewport content trigger.
         *
         * @depreciated Please use visibilityService or intersection observer
         * @type {Observable<Event>}
         */
        // viewport: Subject<Event | void>;

        /**
         * Actual scroll (not just the viewport change)
         *
         * @type {Observable<Event>}
         */
        scroll: Subject<Event | void>;
    };

    /**
     * On resize callback syncing size state information.
     *
     * @protected
     * @param {Event} [event]
     * @memberof Client
     */
    onResize: (event?: Event) => void;

    // onViewport: (event?: Event) => void;

    private cachedWidth?: number;
    private cachedHeight?: number;
    private window = inject(WINDOW);

    readonly rootElement$ = new BehaviorSubject<HTMLElement | undefined>(undefined);
    private rootElementRect!: DOMRectReadOnly;
    scrollElementForEvents: Window | Element = this.documentRef.scrollingElement!;
    protected subscriptions = new Subscription();

    /**
     * Creates an instance of Client.
     *
     * @memberof Client
     */
    constructor(
        private app: App,
        private timeoutService: TimeoutService,
        acl: Acl,
        @Inject(NCore.OPTIONS) private moduleConfig: NCore.IModuleOptions,
        @Inject(DOCUMENT) private documentRef: Document,
        @Inject(WINDOW) window: NDfDomGlobals.Window
    ) {
        // acl is only needed fo isCms to work
        // tslint:disable-next-line: no-unused-expression
        acl;
        app.client = this;

        // non ng-ssr prerender
        if (isPrerenderOld) {
            this.isPrerender = true;
            this.isUniversal = false;
            this.mode = 'legacyPrerender';
        }
        // anything else
        else {
            const mode = window.ssr?.mode;
            // ng-universal v2+ module
            if (mode) {
                this.isUniversal = ['universal', 'ssr'].includes(mode);
                this.isPrerender = ['prerender', 'ssr', 'legacyPrerender'].includes(mode);
                this.mode = mode;
            }
            // legacy universal (without prerender support)
            else {
                this.isUniversal = !app.isPlatformBrowser;
                this.isPrerender = false;
                this.mode = app.isPlatformBrowser ? 'browser' : 'universal';
            }
        }

        this.cachedWidth = this.width;
        this.cachedHeight = this.height;

        if (app.isPlatformBrowser) {
            let rootSusbscriptions: Subscription[] = [];

            this.subscriptions.add(
                this.rootElement$.subscribe(element => {
                    rootSusbscriptions.forEach(subscription => subscription.unsubscribe());
                    rootSusbscriptions = [];

                    if (element) {
                        rootSusbscriptions.push(
                            resizeObservable(element, event => {
                                this.rootElementRect = event.contentRect;
                                this.update();
                            }).subscribe()
                        );

                        rootSusbscriptions.push(
                            merge(
                                fromEvent(this.scrollElementForEvents, 'scroll'),
                                fromEvent(this.scrollElementForEvents, 'touchmove')
                            ).subscribe((event: Event) => {
                                this.obs.scroll.next(event);
                            })
                        );
                    }
                })
            );
        }

        this.isBot =
            typeof this.window.navigator?.userAgent === 'string' && /HeadlessChrome|prerender/i.test(this.window.navigator?.userAgent);

        this.isTouchy = app.isPlatformBrowser
            ? 'ontouchstart' in this.window ||
              ((<any>this.window).DocumentTouch && this.documentRef instanceof (<any>this.window).DocumentTouch)
            : ['S', 'M'].includes(this.getSizeByWidth(this.window.innerWidth));

        this.obs = {
            // create resize observable
            resize: new Subject<Event | void>(),
            // create breakpoint subject
            breakpoint: new Subject<ISizeName>(),
            // create viewport subject
            // viewport: new Subject<Event | void>(),
            // scroll event subject
            scroll: new Subject<Event | void>()
        };

        this.onResize = this.timeoutService.throttle(
            (event?: Event) => {
                const newWidth = this.width;
                const newHeight = this.height;

                if (newWidth !== this.cachedWidth || newHeight !== this.cachedHeight) {
                    this.update();

                    this.obs.resize.next(event);

                    this.cachedWidth = newWidth;
                    this.cachedHeight = newHeight;
                }
            },
            100,
            { trailing: true, leading: false }
        );

        this.isRetina =
            (app.isPlatformBrowser && (<Window>this.window).devicePixelRatio > 1) ||
            ((<Window>this.window).matchMedia && (<Window>this.window).matchMedia(RETINA_MEDIA_QUERY).matches);

        // initiate
        this.update();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.rootElement$.complete();
        this.isCms$.complete();
        this.isCmsPreview$.complete();
        this.obs.resize.complete();
        this.obs.breakpoint.complete();
        this.obs.scroll.complete();
    }

    setRootElement(element: HTMLElement) {
        if (this.rootElement$.value !== element) {
            this.scrollElementForEvents = element === this.documentRef.scrollingElement ? (this.window as Window) : element;
            this.rootElement$.next(element);
        }
    }

    getRootElement(): HTMLElement {
        return this.rootElement$.value!;
    }
    get isCms() {
        return this.isCms$.value;
    }

    set isCms(value: boolean) {
        this.isCms$.next(value);
    }

    get isCmsPreview() {
        return this.isCmsPreview$.value;
    }

    set isCmsPreview(value: boolean) {
        this.isCmsPreview$.next(value);
    }

    get width(): number {
        if (this.rootElementRect) {
            return Math.floor(this.rootElementRect.width);
        }
        const width =
            typeof document === 'undefined'
                ? this.window.innerWidth
                : this.window.innerWidth && document.documentElement.clientWidth
                  ? Math.min(this.window.innerWidth, document.documentElement.clientWidth)
                  : this.window.innerWidth || document.documentElement.clientWidth || document.getElementsByTagName('body')[0].clientWidth;
        return Math.floor(width);
    }

    get height(): number {
        if (this.rootElementRect) {
            return Math.floor(this.rootElementRect.height);
        }
        const height =
            typeof document === 'undefined'
                ? this.window.innerHeight
                : this.window.innerHeight && document.documentElement.clientHeight
                  ? Math.min(this.window.innerHeight, document.documentElement.clientHeight)
                  : this.window.innerHeight ||
                    document.documentElement.clientHeight ||
                    document.getElementsByTagName('body')[0].clientHeight;

        return Math.floor(height);
    }

    /**
     * @memberof Client
     */
    update(width: number = this.width): void {
        const oldSize = this.size;
        const size = this.getSizeByWidth(width);

        if (!size) {
            this.app.logger.error('Execution problem, size cannot be determined!');
            return;
        }

        this.size = size;

        this.sizeId = Object.keys(this.moduleConfig.breakpoints).indexOf(this.size) as ISizeId;

        this.isS = this.size === 'S';
        this.isM = this.size === 'M';
        this.isL = this.size === 'L';
        this.isX = this.size === 'X';

        // if resized then notify
        this.onResize();

        // if size change notify
        if (this.size !== oldSize) {
            this.obs.breakpoint.next(this.size);
        }
    }

    /**
     * Returns a configured breakpoint by width
     * @param width number
     * @returns {ISizeName}
     */
    getSizeByWidth(width: number): ISizeName {
        return findLast(
            Object.keys(this.moduleConfig.breakpoints).map(key => key as ISizeName),
            key => width >= this.moduleConfig.breakpoints[key].width
        )!;
    }
}

/**
 * Register service in app
 */
declare module '@df/core/app' {
    interface App {
        client: Client;
    }
}
